import { FollowButtonAction, FollowType, isFollowType, notEmpty } from "@product/scmp-sdk";
import * as Sentry from "@sentry/browser";
import { useAtomValue } from "jotai";
import intersection from "lodash/intersection";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback, useEffect } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import type { DeepNonNullable } from "utility-types";

import { useUserFollow } from "~/components/follow-button/hooks";
import type { FollowQueryStringPayload } from "~/components/follow-button/types";
import { computeCompletionAction } from "~/components/follow-button/utils";
import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { data } from "~/data";
import { accountAtom } from "~/lib/account";
import { useRouterReplaceWithSanitizedKeys } from "~/lib/router/hooks";
import { parseArray } from "~/lib/utils";
import type { hooksFollowTargetEntityQuery } from "~/queries/__generated__/hooksFollowTargetEntityQuery.graphql";

import { SanitizedUrlKeys } from "./consts";

export const useFollowActionRedirect = () => {
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);

  const { handleFollowAction } = useUserFollow();
  const { pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();

  const { fetchConsolidateFollowTargets } = useConsolidateFollowTargets();

  const execute = useCallback(
    async (
      currentLocation: string,
      followTargetEntityIds: DeepNonNullable<FollowQueryStringPayload>["action"]["follow"],
      source?: string,
      utmSource?: string,
    ) => {
      const followTargets = Object.entries(followTargetEntityIds).reduce(
        (result, [type, entityIds]) => {
          if (!isFollowType(type)) return result;
          result[type] = parseArray(entityIds);
          return result;
        },
        {
          author: [],
          section: [],
          topic: [],
        } as FollowTargetEntityIds,
      );
      const consolidatedFollowTargets = await fetchConsolidateFollowTargets(followTargets);

      Object.entries(consolidatedFollowTargets)
        .map(([type, consolidatedFollowTarget]) => {
          if (!isFollowType(type) || consolidatedFollowTarget.filteredEntityIds.length === 0)
            return null;
          return {
            consolidatedFollowTarget,
            type,
          };
        })
        .filter(notEmpty)
        .forEach(({ consolidatedFollowTarget, type }, index, validEntries) => {
          if (!user) {
            const { description, title } = getLoginDialogTitleAndDescription({
              eventLabel: consolidatedFollowTarget.eventLabel,
              type,
              utmSource,
            });

            openLoginDialog({
              description,
              destination: currentLocation,
              ga4CustomParameter: {
                follow_name: consolidatedFollowTarget.eventLabel,
                follow_type: type,
                trigger_point: "follow",
              },
              title,
            });
            return;
          }

          handleFollowAction(
            consolidatedFollowTarget.filteredEntityIds,
            type,
            FollowButtonAction.Follow,
            () => {
              sendGATracking({
                action: computeCompletionAction(FollowButtonAction.Follow, type, source),
                category: "Notification Center",
                label: consolidatedFollowTarget.eventLabel,
              });
              // If this is the last entries, replace the url
              if (validEntries.length - 1 === index) {
                void replace(
                  SanitizedUrlKeys,
                  { pathname, query },
                  {
                    shallow: true,
                  },
                );
              }
            },
          );
        });
    },
    [
      fetchConsolidateFollowTargets,
      handleFollowAction,
      openLoginDialog,
      pathname,
      query,
      replace,
      user,
    ],
  );

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as FollowQueryStringPayload;
    if (!parsedQueryString.action?.follow) return;
    void execute(
      window.location.href,
      parsedQueryString.action.follow,
      parsedQueryString.source,
      parsedQueryString.utm_source,
    );
  }, [execute, pathname, query, replace]);
  return null;
};

const useConsolidateFollowTargets = () => {
  const environment = useRelayEnvironment();
  return {
    fetchConsolidateFollowTargets: useCallback(
      async (targetEntityIds: FollowTargetEntityIds) => {
        const computeFallbackConsolidatedFollowTarget = (followType: FollowType) => ({
          eventLabel: "",
          filteredEntityIds: targetEntityIds[followType],
        });
        const fallback = {
          [FollowType.Author]: computeFallbackConsolidatedFollowTarget(FollowType.Author),
          [FollowType.Section]: computeFallbackConsolidatedFollowTarget(FollowType.Section),
          [FollowType.Topic]: computeFallbackConsolidatedFollowTarget(FollowType.Topic),
        };

        try {
          const response = await fetchQuery<hooksFollowTargetEntityQuery>(
            environment,
            graphql`
              query hooksFollowTargetEntityQuery(
                $authorEntityIds: [String!]!
                $sectionEntityIds: [String!]!
                $topicEntityIds: [String!]!
                $topicApplicationId: String!
              ) {
                author: authors(filter: { entityIds: $authorEntityIds }, first: 10) {
                  edges {
                    node {
                      entityId
                      name
                    }
                  }
                }
                section: sections(filter: { entityIds: $sectionEntityIds }, first: 10) {
                  edges {
                    node {
                      entityId
                      name
                    }
                  }
                }
                topic: topics(
                  filter: { entityIds: $topicEntityIds }
                  first: 10
                  applicationId: $topicApplicationId
                ) {
                  edges {
                    node {
                      entityId
                      name
                    }
                  }
                }
              }
            `,
            {
              authorEntityIds: targetEntityIds.author,
              sectionEntityIds: targetEntityIds.section,
              topicApplicationId: data.application.scmp.entityUuid,
              topicEntityIds: targetEntityIds.topic,
            },
          ).toPromise();

          if (!response) return fallback;

          const computeConsolidatedFollowTarget = (followType: FollowType) => ({
            eventLabel: response[followType].edges.map(entity => entity.node.name).join(", ") ?? "",
            filteredEntityIds: intersection<string>(
              targetEntityIds[followType],
              response[followType].edges.map(entity => entity.node.entityId),
            ),
          });

          return {
            [FollowType.Author]: computeConsolidatedFollowTarget(FollowType.Author),
            [FollowType.Section]: computeConsolidatedFollowTarget(FollowType.Section),
            [FollowType.Topic]: computeConsolidatedFollowTarget(FollowType.Topic),
          } as ConsolidatedFollowTargets;
        } catch (error) {
          Sentry.captureException(error);
          return fallback;
        }
      },
      [environment],
    ),
  };
};

type FollowTargetEntityIds = {
  [FollowType.Author]: string[];
  [FollowType.Section]: string[];
  [FollowType.Topic]: string[];
};

type ConsolidatedFollowTargets = {
  [FollowType.Author]: ConsolidatedFollowTarget;
  [FollowType.Section]: ConsolidatedFollowTarget;
  [FollowType.Topic]: ConsolidatedFollowTarget;
};

type ConsolidatedFollowTarget = {
  eventLabel: string;
  filteredEntityIds: string[];
};

interface GetLoginDialogTitleAndDescriptionParameters {
  eventLabel: string;
  type: FollowType;
  utmSource?: string;
}

const getLoginDialogTitleAndDescription = (
  parameters: GetLoginDialogTitleAndDescriptionParameters,
) => {
  const { eventLabel, type, utmSource } = parameters;

  const isFromAmp = utmSource === "google_amp";
  const isFromAppleNews = utmSource === "apple_news";

  if (type === FollowType.Topic && (isFromAmp || isFromAppleNews)) {
    return {
      description: `Log in or register for free in order to follow <span class="bold-text">${eventLabel}</span> and be notified when new content is published.`,
      title: "Keep updated",
    };
  }

  return {
    description: `Log in or register to follow <span class="bold-text">${eventLabel}</span> and get notified on newly published stories.`,
    title: `Get notified on your favourite ${type}`,
  };
};
